import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const de = (iso) => (iso ? iso.split("-").reverse().join(".") : "—");
const daysLeft = (iso) => (!iso ? null : Math.ceil((new Date(`${iso}T12:00:00`) - new Date()) / 86400000));

function SessionsInput({ c, onSaved }) {
  const [v, setV] = useState(c.planner_sessions || "");
  const save = async () => {
    try { await api.put(`/planner/client/${c.client_id}/sessions`, { sessions: v ? +v : null }); toast.success("Terminanzahl gespeichert"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <span className="inline-flex gap-1">
      <input type="number" min={1} max={30} value={v} onChange={(e) => setV(e.target.value)} className="input-tech w-16 py-1 text-xs" placeholder="Anz." data-testid={`planner-sessions-${c.client_id}`} />
      <button onClick={save} className="text-[10px] px-2 border border-slate-300 hover:bg-slate-100" data-testid={`planner-sessions-save-${c.client_id}`}>OK</button>
    </span>
  );
}

function Progress({ c }) {
  const req = (c.tight ? c.existing + c.drafts + (c.plan_sessions || 0) : c.required) || 0;
  const pct = (n) => (req ? `${Math.min(100, (100 * n) / req)}%` : "0%");
  return (
    <div className="w-40">
      <div className="h-2 bg-slate-100 flex overflow-hidden" title={`${c.existing} im Kalender · ${c.drafts} vorgeschlagen`}>
        <div className="bg-slate-400" style={{ width: pct(c.existing) }} />
        <div className="bg-violet-500" style={{ width: pct(c.drafts) }} />
      </div>
      <div className="text-[11px] text-slate-500 mt-1 font-mono">{c.existing} + {c.drafts} / {req || "?"} Termine</div>
    </div>
  );
}

function Status({ c }) {
  if (c.needs_count) return <SessionsInput c={c} onSaved={c._reload} />;
  if (!c.ready) return <span className="text-[11px] text-slate-500">Wartet auf Übergabe</span>;
  if (c.at_risk) return <span className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-700" data-testid={`planner-risk-${c.client_id}`}><AlertTriangle size={12} /> {c.missing} Termin(e) bis zur Frist nicht schaffbar</span>;
  if (c.tight && c.remaining > 0) return <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700" data-testid={`planner-tight-${c.client_id}`}><AlertTriangle size={12} /> Enge Zeit: verdichtet auf {c.ue_per_session} UE/Termin ({c.plan_sessions} Termine)</span>;
  if (c.remaining > 0) return <span className="text-[11px] text-amber-700">{c.remaining} noch zu planen</span>;
  return <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><CheckCircle2 size={12} /> Vollständig geplant</span>;
}

export default function PlannerClientTable({ clients, picked, onPick, onReload, ownerName }) {
  return (
    <div className="border border-slate-200 bg-white mb-6 overflow-x-auto" data-testid="planner-clients">
      <div className="px-5 py-3 border-b border-slate-100 text-[10px] font-bold uppercase tracking-overline text-slate-500">{ownerName ? `Kunden von ${ownerName}` : "Meine Kunden"} ({clients.length}) – zum gezielten Planen auswählen</div>
      {clients.length === 0 && <div className="px-5 py-4 text-xs text-slate-500">Dir sind aktuell keine Kunden zugewiesen.</div>}
      {clients.length > 0 && (
        <table className="w-full text-sm min-w-[820px]">
          <thead><tr className="text-left text-[10px] uppercase tracking-overline text-slate-500 border-b border-slate-100">
            <th className="px-5 py-2 w-8" /><th className="py-2">Kunde</th><th className="py-2">Meine UE</th><th className="py-2">Termine</th><th className="py-2">Fertig bis</th><th className="py-2 pr-5">Status</th>
          </tr></thead>
          <tbody>
            {clients.map((c) => {
              const left = daysLeft(c.deadline_date);
              return (
                <tr key={c.client_id} className={`border-b border-slate-50 ${c.at_risk ? "bg-rose-50/60" : ""}`} data-testid={`planner-client-${c.client_id}`}>
                  <td className="px-5 py-2.5"><input type="checkbox" checked={picked.includes(c.client_id)} onChange={() => onPick(c.client_id)} data-testid={`planner-pick-${c.client_id}`} /></td>
                  <td className="py-2.5"><Link to={`/avgs/${c.client_id}`} className="font-medium text-slate-900 hover:underline">{c.name}</Link>
                    <div className="text-[11px] text-slate-500">Coach {c.my_part}{c.parts_total > 1 ? ` von ${c.parts_total}` : ""} · Maßnahme bis {de(c.end_date)}</div></td>
                  <td className="py-2.5 font-mono" data-testid={`planner-client-ue-${c.client_id}`}>{c.my_ue ?? 0} UE{c.ue_per_session != null && <div className={`text-[11px] font-sans ${c.tight ? "text-amber-700" : "text-slate-500"}`}>≈ {c.ue_per_session} UE/Termin{c.tight ? " (verdichtet)" : ""}</div>}</td>
                  <td className="py-2.5"><Progress c={c} /></td>
                  <td className="py-2.5" data-testid={`planner-client-deadline-${c.client_id}`}><div className="font-medium">{de(c.deadline_date)}</div>
                    {left !== null && <div className={`text-[11px] ${left <= 21 ? "text-rose-700" : "text-slate-500"}`}>noch {left} Tage · {c.free_days_until_deadline} freie Tage</div>}</td>
                  <td className="py-2.5 pr-5"><Status c={{ ...c, _reload: onReload }} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
